import { RootState } from "common/reducers";
import { createSelector } from "reselect";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { List } from "immutable";
import { SignedDocumentRecord } from "common/records/SignedDocument";
import { Nullable } from "common/types";

const model = "signedDocuments";

// Stable options ref so getRecordsByModel's props-keyed memoization isn't defeated by a fresh literal each call.
const SIGNED_DOCUMENTS_OPTIONS = { modelName: model, path: [model, "byId"], includeArchived: true } as const;

export const signedDocumentList = createSelector(
  [(state: RootState) => getRecordsByModel(state, SIGNED_DOCUMENTS_OPTIONS)],
  (records): List<SignedDocumentRecord> => records,
);

export const byId = (state: RootState, _props?: any) => state.getIn([model, "byId"]);

export const signedDocument = (state: RootState, props?: any): Nullable<SignedDocumentRecord> =>
  byId(state).get(props.signedDocumentId);

/**
 * Checks if all recipients have signed the document
 *
 * @param {RootState} state The RootState
 *  @param {{ signedDocumentId: number }} options
 * @returns {boolean} boolean
 */
export const allSigned = createSelector([signedDocument], (sd) => {
  if (!sd) return false;

  const unsigned = sd.recipients.find((r) => {
    return r.status !== "recipient_signed";
  });

  return unsigned === undefined;
});

/**
 * Retrieves the current signed document ID
 *
 * @param {RootState} state The RootState
 * @returns {number} The current signed document ID
 */
export const currentSignedDocumentId = (state: RootState): number => state.getIn(["signedDocuments", "currentId"]);

/**
 * Retrieves the current signed document
 *
 * @param {RootState} state The RootState
 * @returns {SignedDocumentRecord | undefined} The current signed document or undefined if not found
 */
export const currentSignedDocument = createSelector([byId, currentSignedDocumentId], (byId, currentId) => {
  return byId.get(currentId);
});
